import React, { useEffect, useState } from "react";
import { ConnectedProps, connect } from "react-redux";
import { extendedFields } from "us.collection.admin/actions";
import Common from "us.common";
import { useTranslation } from "react-i18next";
import { RootState } from "us.helper/types";
import { PlusOutlined, MoreOutlined } from "us.icons";
import { ExtendedFieldDetails, ItemMenu } from "./Components";
import {
  EXTENDED_FIELD_DRAWER_DEFAULT,
  ExtendedFieldsActions,
} from "us.collection.admin/constants/ExtendedFields";
import "./Home.scss";
import { useSafeBackNavigation } from "us.helper/navigation";

const {
  $Skeleton,
  $TableTree,
  $DateLabel,
  $Tooltip,
  $Button,
  $Drawer,
  $Popover,
  $Affix,
  $PageHeader,
  $Divider,
} = Common.Components;

/**
 * @description - Extended fields main component
 * @link Design Document - https://unicorn-solutions.atlassian.net/browse/UBX4-23848
 * @param {PropsFromRedux} props
 * @return {JSX.Element}
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 18/03/2025
 */
const ExtendedFields: React.FC<PropsFromRedux> = (
  props: PropsFromRedux,
): JSX.Element => {
  const { t } = useTranslation(["US.COLLECTION.ADMIN"]);
  const goBack = useSafeBackNavigation();

  const { fields, drawerInfo, getFields, manageDrawer, deleteField } = props;

  const [itemMenu, setItemMenu] = useState<{
    visible: boolean;
    fieldId: number;
  }>({ visible: false, fieldId: -1 });

  useEffect(() => {
    getFields && getFields({});
  }, []);

  // auto close item menu after 5 seconds
  const setAutoClose = (fieldId: number) => {
    setTimeout(() => {
      setItemMenu({
        visible: false,
        fieldId,
      });
    }, 8000);
  };

  /**
   * Determines the available actions for a given extended field.
   *
   * @param extendedField - The extended field object to evaluate.
   * @returns An array of `ExtendedFieldsActions` indicating the actions
   *          that can be performed on the extended field. If the field name
   *          is "DebtorStatus", only the `EDIT` action is returned. Otherwise,
   *          both `EDIT` and `DELETE` actions are returned.
   */
  const getExtendedFieldActions = (
    extendedField: any,
  ): ExtendedFieldsActions[] => {
    if (extendedField?.fieldName === "DebtorStatus") {
      return [ExtendedFieldsActions.EDIT];
    } else {
      return [ExtendedFieldsActions.EDIT, ExtendedFieldsActions.DELETE];
    }
  };

  const columns: any = [
    {
      title: "",
      key: "more",
      dataIndex: "more",
      width: 50,
      customRenderChild: (text: any, record: any) => {
        return (
          <div className="align-items-center" key={0}>
            <$Popover
              placement="bottomLeft"
              trigger="click"
              open={itemMenu.visible && itemMenu.fieldId === record.id}
              onVisibleChange={(visible: boolean) => {
                setItemMenu({
                  ...itemMenu,
                  visible,
                  fieldId: record.id,
                });
                setAutoClose(0);
              }}
              content={
                <ItemMenu
                  actions={getExtendedFieldActions(record)}
                  onCallAction={(actionType: ExtendedFieldsActions) => {
                    setItemMenu({
                      ...itemMenu,
                      visible: false,
                      fieldId: record.id,
                    });
                    onHandleTableAction(actionType, record);
                  }}
                />
              }
              destroyTooltipOnHide
            >
              <$Button
                data-testid="popover-btn"
                icon={<MoreOutlined />}
                size="small"
              />
            </$Popover>
          </div>
        );
      },
    },
    {
      title: t("US.COLLECTION.ADMIN:EXTENDED_FIELDS.FIELD_NAME"),
      dataIndex: "fieldName",
      key: "fieldName",
      width: "300px",
      customSorter: (a: any, b: any) => a.localeCompare(b),
      customFilter: true,
      ellipsis: { showTitle: false },
      customRenderChild: (text: any) => (
        <$Tooltip placement="topLeft" title={text} key={text}>
          {text}
        </$Tooltip>
      ),
    },
    {
      title: t("US.COLLECTION.ADMIN:EXTENDED_FIELDS.CATEGORY"),
      dataIndex: "fieldCategory",
      key: "fieldCategory",
      customSorter: (a: any, b: any) => a.localeCompare(b),
      customFilter: true,
      ellipsis: { showTitle: false },
      customRenderChild: (text: any) => (
        <$Tooltip placement="topLeft" title={text} key={text}>
          {text}
        </$Tooltip>
      ),
    },
    {
      title: t("US.COLLECTION.ADMIN:EXTENDED_FIELDS.DESCRIPTION"),
      dataIndex: "description",
      key: "description",
      width: "300px",
      customSorter: (a: any, b: any) => a.localeCompare(b),
      customFilter: true,
      ellipsis: { showTitle: false },
      customRenderChild: (text: any) => (
        <$Tooltip placement="topLeft" title={text} key={text}>
          {text}
        </$Tooltip>
      ),
    },
    {
      title: t("US.COLLECTION.ADMIN:EXTENDED_FIELDS.VALUE_TYPE"),
      dataIndex: "dataType",
      key: "dataType",
      customSorter: (a: any, b: any) => a.localeCompare(b),
      customFilter: true,
      ellipsis: { showTitle: false },
      customRenderChild: (text: any) => (
        <$Tooltip placement="topLeft" title={text} key={text}>
          {text}
        </$Tooltip>
      ),
    },
    {
      title: t("US.COLLECTION.ADMIN:EXTENDED_FIELDS.MODIFIED_USER"),
      dataIndex: "modifiedUser",
      key: "modifiedUser",
      customSorter: (a: any, b: any) => a.localeCompare(b),
      customFilter: true,
      ellipsis: { showTitle: false },
      customRenderChild: (text: any) => (
        <$Tooltip placement="topLeft" title={text} key={text}>
          {text}
        </$Tooltip>
      ),
    },
    {
      title: t("US.COLLECTION.ADMIN:EXTENDED_FIELDS.MODIFIED_DATE"),
      dataIndex: "modifiedDate",
      key: "modifiedDate",
      customSorter: (a: any, b: any) =>
        new Date(a).getTime() - new Date(b).getTime(),
      customFilter: "date",
      customRenderChild: (text: any) => {
        return (
          <span>
            <$DateLabel value={text} />
          </span>
        );
      },
    },
  ];

  /**
   * @description handle extended field menu actions
   * @param action - action of clicked menu icon
   * @param record - selected filed for action
   */
  const onHandleTableAction = (action: ExtendedFieldsActions, record: any) => {
    const { id } = record;
    switch (action) {
      case ExtendedFieldsActions.EDIT:
        openDrawer(ExtendedFieldsActions.EDIT, id);
        break;
      case ExtendedFieldsActions.DELETE:
        deleteField &&
          deleteField({
            fieldId: id,
          });
        break;
      default:
        break;
    }
  };

  /**
   * @description Open the drawer by type
   * @param {DrawerType} type - Type of the drawer
   * @param {number} id - Selected field id
   */
  const openDrawer = (type: ExtendedFieldsActions, id: number) => {
    // decide the suitable title for the drawer
    let title =
      type === ExtendedFieldsActions.EDIT
        ? t(`US.COLLECTION.ADMIN:EXTENDED_FIELDS.EDIT_EXTENDED_FIELD`)
        : t("US.COLLECTION.ADMIN:EXTENDED_FIELDS.NEW_EXTENDED_FIELD");

    // change drawer state
    manageDrawer &&
      manageDrawer({
        type,
        title,
        id,
      });
  };

  /**
   * @description - Close the drawer
   */
  const closeDrawer = () => {
    manageDrawer && manageDrawer(EXTENDED_FIELD_DRAWER_DEFAULT);
  };

  return (
    <div className="extended-fields">
      <$Affix offsetTop={48}>
        <div className="page-header header-border">
          <div className="d-flex align-items-center">
            <$PageHeader
              className="px-0"
              onBack={goBack}
              title={t("US.COLLECTION.ADMIN:EXTENDED_FIELDS.EXTENDED_FIELDS")}
            />
            <$Divider className="bui-devider" type="vertical" />
            <$Button
              className="mr-2"
              type="default"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => openDrawer(ExtendedFieldsActions.NEW, -1)}
            >
              {t("US.COLLECTION.ADMIN:EXTENDED_FIELDS.NEW_EXTENDED_FIELD")}
            </$Button>
          </div>
        </div>
      </$Affix>
      <div className="space-content">
        <$Skeleton loading={fields.isLoading} active paragraph={{ rows: 2 }}>
          <$TableTree
            rowKey="id"
            data={fields.data}
            size="small"
            className="mt-3"
            onSort={(sortData, dataSource) => {
              return sortData(dataSource);
            }}
            onFilter={(searchData, dataSource) => {
              return searchData(dataSource);
            }}
            filterOnType={true}
            resetOnSourceChange={true}
            bordered
            pagination={{ defaultPageSize: 20 }}
            virtual={true}
            scroll={{ x: "100%", y: "calc(100vh - 195px)" }}
            columns={columns}
            firstColSkipFilterProps={-1}
            onRow={(record, _rowIndex) => {
              return {
                onDoubleClick: () =>
                  onHandleTableAction(ExtendedFieldsActions.EDIT, record),
              };
            }}
          />
        </$Skeleton>
      </div>
      <$Drawer
        title={drawerInfo.title}
        width={"800px"}
        open={drawerInfo.visible}
        onClose={closeDrawer}
        destroyOnClose={true}
        maskClosable={false}
      >
        <ExtendedFieldDetails onCancel={closeDrawer} />
      </$Drawer>
    </div>
  );
};

const mapState = (state: RootState) => {
  const { common, extendedField } = state;
  const { fields, drawerInfo } = extendedField;

  return {
    currentLanguage: common.currentLanguage,
    fields,
    drawerInfo,
  };
};

const { fields, deleteExtendedField } = extendedFields;
const mapDispatch = {
  manageDrawer: fields.openDrawer,
  getFields: fields.get,
  deleteField: deleteExtendedField.save,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ExtendedFields);
